/** Pure rules shared by the web and native event ticket screens. */
import { eventLimitProblem } from "@libs/checkout-core";
import { adValueFromMinor, minorToMajor, storeMoney } from "@libs/platform-wire";

export type EventTier = {
  engineItemId: string;
  name: string;
  priceMinor: number;
  currency: string;
  saleStartsAt: string | null;
  saleEndsAt: string | null;
  /** Waiting on another tier to run out, with no opening time known
   *  (LIS-912). `saleStartsAt` already carries the engine's opening time. */
  saleAwaitingTrigger?: boolean;
  soldOut: boolean;
  gated: boolean;
  active: boolean;
  /** The organiser approves each request: the tier is asked for through its
   *  waitlist, never put in a basket. */
  requiresApproval?: boolean;
  /** The most one order may hold; absent or null leaves the screen's own. */
  maxPerOrder?: number | null;
  /** How its amounts are counted: in ISO 4217's minor unit, as a vendor's
   *  (1-tap) are (`iso4217`), or, absent, in the ticketing engine's own. */
  minorUnits?: MinorUnits;
  /** The price before a fan club member's discount (a ticket discount,
   *  LIS-1177, or a club discount code, LIS-1178), struck through beside
   *  `priceMinor`, the member's price; absent without one. */
  listPriceMinor?: number;
};

/** What a fan reads on a members' presale (a gated tier, LIS-1156): every
 *  fan sees it, and only a current member of the headliner's fan club can
 *  buy it; the checkout refuses anyone else. */
export const MEMBERS_PRESALE = "Members' presale";
/** What a fan reads beside the price their fan club ticket discount gives
 *  them (LIS-1177). */
export const MEMBER_PRICE = "Member price";

/**
 * The tier as a fan with a club ticket discount buys it (LIS-1177): at the
 * member price the engine quoted them (`memberPriceMinor`, absent where it
 * quoted none below the list price), keeping the list price to show beside
 * it. A tier bought later at a checkout the engine makes for the fan's
 * address alone (a request, or a sold-out tier's waitlist) names no member,
 * so it keeps its list price.
 */
export function memberPricedTier<
  T extends Pick<EventTier, "priceMinor" | "soldOut" | "requiresApproval">,
>(tier: T, memberPriceMinor: number | undefined): T & { listPriceMinor?: number } {
  if (tier.requiresApproval || tier.soldOut) return tier;
  if (memberPriceMinor === undefined || memberPriceMinor >= tier.priceMinor)
    return tier;
  return {
    ...tier,
    priceMinor: memberPriceMinor,
    listPriceMinor: tier.priceMinor,
  };
}

/** A tier's note, led by the presale's label when it is one. */
export const tierLabel = (
  tier: Pick<EventTier, "gated">,
  note: string | null,
) => (tier.gated ? [MEMBERS_PRESALE, note].filter(Boolean).join(" · ") : note);

/** How a tier's amounts are counted: the ticketing engine's own minor unit
 *  (`engine`: Intl's decimals, as `adValueFromMinor` reads them), or ISO
 *  4217's (`iso4217`: a vendor's, money.ts). They differ only for the few
 *  currencies whose minor unit Intl drops (IQD, RSD and eleven more). */
export type MinorUnits = "engine" | "iso4217";
/** Whether the event asks a fan for a detail beyond their email. */
export type EventDetailRule = "off" | "optional" | "required";
export type EventDetailRules = { name: EventDetailRule; phone: EventDetailRule };
/** A contact number as typed: the engine's own shape for one. */
const PHONE = /^(?=(?:\D*\d){5})\+?[\d ()-]{7,25}$/;
/** A phone given must look like one; one left out must not be required. */
export const phoneReady = (rule: EventDetailRule, phone: string) =>
  rule === "off" ||
  (phone.trim() === "" ? rule !== "required" : PHONE.test(phone.trim()));
/** The details a checkout may go ahead with, under the event's rules. */
export const detailsReady = (
  rules: EventDetailRules,
  details: { name: string; phone: string },
) =>
  (rules.name !== "required" || details.name.trim() !== "") &&
  phoneReady(rules.phone, details.phone);
export type EventLine = { item_id: string; quantity: number };

/** Whether the tier's sale has opened: not still waiting on an opening
 *  trigger, and past its opening time (the engine's, which is the start date
 *  or the trigger's instant, whichever is first). */
export const tierOpened = (
  tier: Pick<EventTier, "saleStartsAt" | "saleAwaitingTrigger">,
  now: number,
) =>
  !tier.saleAwaitingTrigger &&
  (!tier.saleStartsAt || Date.parse(tier.saleStartsAt) <= now);

export const tierOnSale = (tier: EventTier, now: number) =>
  !tier.soldOut &&
  tierOpened(tier, now) &&
  (!tier.saleEndsAt || Date.parse(tier.saleEndsAt) > now);

/** On sale and bought the ordinary way; a tier that takes requests is not. */
export const tierBuyable = (tier: EventTier, now: number) =>
  tierOnSale(tier, now) && !tier.requiresApproval;
/** How many of a tier one order may hold. */
export const tierMax = (tier: EventTier | undefined) =>
  tier?.maxPerOrder ?? 100;

/** A tier as a fan meets it. One that takes requests is asked for while it is
 *  on sale, places left or not, since the organiser decides who gets one:
 *  only an ordinary tier is ever sold out to a fan. */
export const tierForFan = (tier: EventTier): EventTier =>
  tier.requiresApproval && tier.soldOut ? { ...tier, soldOut: false } : tier;

export type TierAvailability = "available" | "not-yet" | "ended" | "sold-out";
export const tierAvailability = (
  tier: EventTier,
  now: number,
): TierAvailability =>
  tier.soldOut
    ? "sold-out"
    : !tierOpened(tier, now)
      ? "not-yet"
      : tier.saleEndsAt && Date.parse(tier.saleEndsAt) <= now
        ? "ended"
        : "available";

export const linesFrom = (
  tiers: EventTier[],
  quantities: Record<string, number>,
  now: number,
): EventLine[] =>
  tiers
    .filter((t) => tierBuyable(t, now) && quantities[t.engineItemId])
    .map((t) => ({
      item_id: t.engineItemId,
      quantity: quantities[t.engineItemId]!,
    }));

export const totalMinor = (tiers: EventTier[], lines: EventLine[]) =>
  lines.reduce(
    (sum, line) =>
      sum +
      (tiers.find((t) => t.engineItemId === line.item_id)?.priceMinor ?? 0) *
        line.quantity,
    0,
  );

/** An amount as the purchase flow says it, in its own currency
 *  (`storeMoney`: "£45.00", "US$45.00", "JPY 4,500"), its minor units counted
 *  as its tiers count them (`minorUnits`; the engine's when absent). */
export const formatMoney = (minor: number, currency: string, units: MinorUnits = "engine") =>
  storeMoney(
    units === "iso4217" ? minorToMajor(minor, currency) : adValueFromMinor(minor, currency),
    currency,
  );
export const formatWhen = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

export const clampQuantity = (next: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, next));

/** The Select tickets primary button: "Continue · £24.00" while the
 *  selection costs something, plain "Continue" for a free or empty one. */
export const continueLabel = (tiers: EventTier[], lines: EventLine[]) => {
  const total = totalMinor(tiers, lines);
  if (total <= 0) return "Continue";
  const chosen = tiers.find((t) => lines.some((l) => l.item_id === t.engineItemId));
  return `Continue · ${formatMoney(total, chosen?.currency ?? "GBP", chosen?.minorUnits)}`;
};

/** An event's per-order ticket limits, counted across all its ticket types
 *  (LIS-924), with the name the checkout says them with. Null limits: none. */
export type EventOrderLimits = {
  name: string;
  minTicketsPerOrder: number | null;
  maxTicketsPerOrder: number | null;
};

/**
 * The selection against the event's per-order limits: how many more tickets
 * fit in it (Infinity without a maximum), and why it cannot continue in the
 * checkout's own words ("Choose at least 2 tickets for …"), or null. An
 * empty selection has nothing to say: Continue waits for a ticket anyway.
 * The per-customer cap is the engine's to refuse; it depends on the
 * customer's past orders.
 */
export function orderLimitState(
  limits: EventOrderLimits | null | undefined,
  lines: EventLine[],
) {
  const total = lines.reduce((sum, line) => sum + line.quantity, 0);
  const max = limits?.maxTicketsPerOrder ?? null;
  return {
    total,
    room: max === null ? Infinity : Math.max(0, max - total),
    problem:
      limits && total > 0
        ? eventLimitProblem({
            name: limits.name,
            total,
            min: limits.minTicketsPerOrder,
            max,
          })
        : null,
  };
}

/** The quantity the sheet opens with on its first ticket type: one (or the
 *  type's own minimum), raised to the event's minimum so Continue is ready,
 *  within the type's and the event's maximum. None when even the type's
 *  minimum does not fit the event. */
export function openingQuantity(
  limits: EventOrderLimits | null | undefined,
  tier: { minQuantity?: number; maxQuantity?: number } = {},
) {
  const min = tier.minQuantity ?? 1;
  const quantity = Math.min(
    Math.max(min, limits?.minTicketsPerOrder ?? 0),
    tier.maxQuantity ?? Infinity,
    limits?.maxTicketsPerOrder ?? Infinity,
  );
  return quantity >= min ? quantity : 0;
}
